import { CART_CHANGE_EVENT, type CartChangeDetail } from '../Service/CartService'
import { type CartItem } from '../Service/CartStorageService'
import { deleteCookie, readCookie } from '../Utility/Cookie'
import { getElement } from '../Utility/Dom'
import { push } from './DataLayer'
import { type Ecommerce, type EcommerceItem } from './Types'

// Written by CheckoutRedirectFinisher on the redirect to the confirmation page.
const PURCHASE_COOKIE = 'vhw_purchase'

// Amounts stay exactly as the middleware delivered them (net, gross for e-learning), so the
// data layer matches what the customer saw. `tax` is never derived.
const toAmount = (value: string): number | undefined => {
  const amount = Number(value)

  return value.trim() === '' || Number.isFinite(amount) === false ? undefined : amount
}

// The VA-Nummer is the SKU across every event; without one an item cannot be attributed, so it
// is dropped rather than reported under a substitute id.
const toItem = (item: CartItem): EcommerceItem | null =>
  item.course.vaNumber === null || item.course.vaNumber === ''
    ? null
    : {
      item_id: item.course.vaNumber,
      item_name: item.course.title,
      price: toAmount(item.unitPrice),
      quantity: item.quantity,
    }

// Satisfied by both `Cart` and `CartChangeDetail`, so callers pass whichever they hold.
type CartSnapshot = {
  items: CartItem[]
  totalAmount: string
}

const toEcommerce = (cart: CartSnapshot): Ecommerce => ({
  items: cart.items.map(toItem).filter((item): item is EcommerceItem => item !== null),
  value: toAmount(cart.totalAmount),
})

// Matomo keeps no cart state, so every cart event carries the whole cart, not just the change.
export const trackAddToCart = (cart: CartSnapshot): void => {
  push({ ecommerce: toEcommerce(cart), event: 'add_to_cart' })
}

export const trackViewCart = (cart: CartSnapshot): void => {
  push({ ecommerce: toEcommerce(cart), event: 'view_cart' })
}

export const observeCheckout = (): void => {
  if (getElement('checkout-form') === null) {
    return
  }

  const onChange = (event: Event): void => {
    const detail = (event as CustomEvent<CartChangeDetail>).detail
    if (detail.items.length === 0) {
      return
    }

    // The first hydrated cart is the checkout entry; later mutations are not a second start.
    document.removeEventListener(CART_CHANGE_EVENT, onChange)
    push({ ecommerce: toEcommerce(detail), event: 'begin_checkout' })
  }

  document.addEventListener(CART_CHANGE_EVENT, onChange)
}

export const trackPurchase = (): void => {
  const raw = readCookie(PURCHASE_COOKIE)
  if (raw === null || raw === '') {
    return
  }

  // Consumed before it is parsed, so a reload can never report the booking twice.
  deleteCookie(PURCHASE_COOKIE)

  try {
    const ecommerce = JSON.parse(raw) as Ecommerce
    push({ ecommerce: { ...ecommerce, currency: 'EUR' }, event: 'purchase' })
  } catch {
    // Nothing recoverable — the booking itself already succeeded server-side.
  }
}
